
import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { AvailableLangs, LangDefinition, TranslocoService } from '@jsverse/transloco';
import { take } from 'rxjs';

@Component({
    selector: 'languages',
    templateUrl: './languages.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'languages',
    imports: [MatButtonModule, MatMenuModule, NgTemplateOutlet],
})
export class LanguagesComponent implements OnInit {
    availableLangs: AvailableLangs;
    activeLang: string;
    flagCodes: any;

    constructor(
        private readonly _fuseNavigationService: FuseNavigationService,
        private readonly _translocoService: TranslocoService,
        private readonly _cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        // Get the available languages from transloco
        this.availableLangs = this._translocoService.getAvailableLangs();

        const storedLanguage = localStorage.getItem('lang');
        const supportedLanguages = ['de', 'en', 'fr', 'it'];

        if (storedLanguage && supportedLanguages.includes(storedLanguage)) {
            this._translocoService.setActiveLang(storedLanguage);
        } else {
            this._translocoService.setActiveLang('de');
            localStorage.setItem('lang', 'de');
        }

        // Subscribe to language changes
        this._translocoService.langChanges$.subscribe((activeLang) => {
            // Get the active lang
            this.activeLang = activeLang;

            // Tell OnPush to re-render now that activeLang changed
            this._cdr.markForCheck();

            // Update the navigation
            this._updateNavigation(activeLang);
        });

        // Set the country iso codes for languages for flags
        this.flagCodes = {
            en: 'us',
            de: 'de',
            fr: 'fr',
            it: 'it',
        };
    }

    setActiveLang(lang: string): void {
        // Set the active lang
        this._translocoService.setActiveLang(lang);
        localStorage.setItem('lang', lang);
    }

    getLangId(lang: string | LangDefinition): string {
        return typeof lang === 'string' ? lang : lang.id;
    }

    getLangLabel(lang: string | LangDefinition): string {
        return typeof lang === 'string' ? lang : lang.label;
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the navigation
     *
     * @param lang
     * @private
     */
    private _updateNavigation(lang: string): void {
        // For the demonstration purposes, we will only update the Dashboard names
        // from the navigation but you can do a full swap and change the entire
        // navigation data.
        //
        // You can import the data from a file or request it from your backend,
        // it's up to you.

        // Get the component -> navigation data -> item
        const navComponent =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                'mainNavigation'
            );

        // Return if the navigation component does not exist
        if (!navComponent) {
            return null;
        }

        // Get the flat navigation data
        const navigation = navComponent.navigation;

        this._updateNavigationItemTitle(navComponent, navigation, 'dashboards.project', 'Project');
        this._updateNavigationItemTitle(navComponent, navigation, 'dashboards.analytics', 'Analytics');
        this._updateNavigationItemTitle(navComponent, navigation, 'accounting', 'Accounting');
        this._updateNavigationItemTitle(navComponent, navigation, 'accounting.cash-book', 'CashBook');
    }

    private _updateNavigationItemTitle(
        navComponent: FuseVerticalNavigationComponent,
        navigation: any[],
        itemId: string,
        translationKey: string
    ): void {
        const item = this._fuseNavigationService.getItem(itemId, navigation);
        if (!item) {
            return;
        }

        this._translocoService
            .selectTranslate(translationKey)
            .pipe(take(1))
            .subscribe((translation) => {
                item.title = translation;
                navComponent.refresh();
            });
    }
}
